import { useState } from "react";
import axios from "axios";
import { API } from "../../config";
import { CheckCircle, ExternalLink, Loader2, Shield, Sparkles } from "lucide-react";

export const BacklinkCheckerTool = ({ t, language, userEmail }) => {
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);

  const handleCheck = async () => {
    if (!url.trim() || isLoading) return;
    setIsLoading(true);
    setResults(null);
    try {
      const res = await axios.post(`${API}/backlink-checker`, {
        url: url.trim().startsWith('http') ? url.trim() : `https://${url.trim()}`,
        language,
        email: userEmail || ''
      });
      setResults(res.data);
    } catch (e) {
      console.error('Backlink check error:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const typeColors = { dofollow: 'bg-green-100 text-green-700', nofollow: 'bg-[var(--color-surface-alt)] text-[var(--color-text-muted)]' };
  const pageColors = { blog: 'bg-[#a855f7]/10 text-[#a855f7]', news: 'bg-purple-900/30 text-purple-400', directory: 'bg-orange-100 text-orange-700', forum: 'bg-cyan-100 text-cyan-700', social: 'bg-pink-100 text-pink-700' };

  return (
    <div data-testid="backlink-checker-tool">
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <input
          type="text"
          data-testid="backlink-url-input"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCheck()}
          placeholder={t.backlink_url_placeholder}
          className="flex-1 px-4 py-3 border-2 border-[var(--color-border)] focus:border-[#a855f7] focus:outline-none transition-colors"
        />
        <button
          data-testid="backlink-check-btn"
          onClick={handleCheck}
          disabled={isLoading || !url.trim()}
          className="px-6 py-3 bg-[#a855f7] text-white font-semibold hover:bg-purple-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Shield className="w-5 h-5" />}
          {t.backlink_check}
        </button>
      </div>

      {results && (
        <div className="space-y-6 animate-fade-in" data-testid="backlink-results">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-center">
              <p className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">{t.backlink_total}</p>
              <p className="text-2xl font-mono font-bold text-white mt-1">{results.total_backlinks.toLocaleString()}</p>
            </div>
            <div className="border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-center">
              <p className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">{t.backlink_dofollow}</p>
              <p className="text-2xl font-mono font-bold text-green-600 mt-1">{results.dofollow_count.toLocaleString()}</p>
            </div>
            <div className="border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-center">
              <p className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">{t.backlink_nofollow}</p>
              <p className="text-2xl font-mono font-bold text-[var(--color-text-muted)] mt-1">{results.nofollow_count.toLocaleString()}</p>
            </div>
            <div className="border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-center">
              <p className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">{t.backlink_domains}</p>
              <p className="text-2xl font-mono font-bold text-[#a855f7] mt-1">{results.referring_domains}</p>
            </div>
            <div className="border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-center">
              <p className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">{t.backlink_score}</p>
              <p className={`text-2xl font-mono font-bold mt-1 ${results.backlink_score >= 70 ? 'text-green-600' : results.backlink_score >= 40 ? 'text-orange-500' : 'text-red-600'}`}>{results.backlink_score}/100</p>
            </div>
          </div>

          {/* Dofollow/Nofollow Bar */}
          <div className="border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
            <div className="flex justify-between text-xs font-medium text-[var(--color-text-muted)] mb-2">
              <span>{t.backlink_dofollow}: {results.dofollow_count}</span>
              <span>{t.backlink_nofollow}: {results.nofollow_count}</span>
            </div>
            <div className="h-3 bg-gray-200 rounded-full overflow-hidden flex">
              <div className="bg-green-500 h-full transition-all" style={{ width: `${results.total_backlinks > 0 ? (results.dofollow_count / results.total_backlinks) * 100 : 50}%` }} />
              <div className="bg-gray-400 h-full transition-all" style={{ width: `${results.total_backlinks > 0 ? (results.nofollow_count / results.total_backlinks) * 100 : 50}%` }} />
            </div>
          </div>

          {/* Top Backlinks Table */}
          <div className="border border-[var(--color-border)] bg-[var(--color-surface)] overflow-hidden">
            <div className="p-4 border-b border-[var(--color-border)] bg-[var(--color-bg)]">
              <h3 className="font-bold flex items-center gap-2 font-['Chivo']">
                <ExternalLink className="w-5 h-5 text-[#a855f7]" />
                {t.backlink_top}
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[var(--color-bg)] border-b border-[var(--color-border)]">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-[var(--color-text-muted)]">{t.backlink_source}</th>
                    <th className="text-left px-4 py-3 font-semibold text-[var(--color-text-muted)]">{t.backlink_anchor}</th>
                    <th className="text-center px-4 py-3 font-semibold text-[var(--color-text-muted)]">DA</th>
                    <th className="text-center px-4 py-3 font-semibold text-[var(--color-text-muted)]">{t.backlink_type}</th>
                    <th className="text-center px-4 py-3 font-semibold text-[var(--color-text-muted)]">{t.backlink_page}</th>
                  </tr>
                </thead>
                <tbody>
                  {results.top_backlinks.map((bl, i) => (
                    <tr key={i} className="border-b border-[var(--color-border)] hover:bg-[var(--color-surface)]/30 transition-colors" data-testid={`backlink-row-${i}`}>
                      <td className="px-4 py-3 font-medium text-[#a855f7] max-w-[200px] truncate">{bl.source_url}</td>
                      <td className="px-4 py-3 text-[var(--color-text-muted)] max-w-[150px] truncate">{bl.anchor_text}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`font-mono font-bold ${bl.domain_authority >= 70 ? 'text-green-600' : bl.domain_authority >= 40 ? 'text-orange-500' : 'text-red-500'}`}>{bl.domain_authority}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${typeColors[bl.link_type] || 'bg-[var(--color-surface-alt)]'}`}>{bl.link_type}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${pageColors[bl.page_type] || 'bg-[var(--color-surface-alt)]'}`}>{bl.page_type}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Anchor Texts + Suggestions */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="border border-[var(--color-border)] bg-[var(--color-surface)]" data-testid="anchor-texts">
              <div className="p-4 border-b border-[var(--color-border)] bg-[var(--color-bg)]">
                <h4 className="font-bold text-sm font-['Chivo']">{t.backlink_anchors}</h4>
              </div>
              <div className="p-4 flex flex-wrap gap-2">
                {results.anchor_texts.map((a, i) => (
                  <span key={i} className="px-3 py-1.5 bg-[var(--color-surface)] text-[#a855f7] text-xs font-medium rounded-full border border-blue-100">{a}</span>
                ))}
              </div>
            </div>
            <div className="border border-[var(--color-border)] bg-[var(--color-surface)]" data-testid="backlink-suggestions">
              <div className="p-4 border-b border-[var(--color-border)] bg-[var(--color-bg)]">
                <h4 className="font-bold text-sm flex items-center gap-2 font-['Chivo']">
                  <Sparkles className="w-4 h-4 text-[#a855f7]" />
                  {t.suggestions}
                </h4>
              </div>
              <div className="p-4 space-y-2">
                {results.suggestions.map((s, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{s}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};



// Site Speed Analyzer Component
